import { Fragment } from 'react';
import type { IActualTab, IProjectMain } from 'models';

/**
 * Left icon tab rail (#menu, MenuView → tmpl/menu.tmpl), inventory §2 "THE TAB
 * BAR" / parity 13–15. One <a> per CMS-permitted tab in master `order`:
 * `<a class="ka-has-tooltip [bottom]" tooltip="{name}" value="{value}"
 * tabindex="-1"><i class="icon-{logo}"></i>{name}</a>`. Files & Notes carry
 * `bottom`. "Report Logo" is NOT a DB tab — appended client-side only when
 * `project.is_can_set_project_logo` (Material Symbols icon).
 *
 * `access=false` does NOT hide a tab (read-only enforcement lives inside each
 * tab view — parity 16). `.selected` is derived from the active tab (Backbone
 * sets it on click AND re-syncs it in changePrjAppViewState).
 *
 * ICONS (dev-sync F / PR #834, commit 46e33d3ca9 "Use icon-{logo} class for
 * AHU components/accessories menu icons"): dev briefly special-cased the two
 * new tabs with a `<i class="material-symbols-outlined">list</i>` branch
 * (ce03c2ea2c, 8e1d600257) and then REVERTED to the plain `icon-{{logo}}`
 * class for every tab, adding `#menu nav a i.icon-list{left:unset;top:unset}`
 * to align the glyph (project/css/sass/_menu.scss:66-71 → the already-merged
 * static/css/project/styles.css:7443-7447). The master rows carry
 * `logo = 'list'`, so this component's existing `icon-${tab.logo}` render IS
 * the #834 end state — no branch needed, only the CSS (already loaded by
 * ProjectBoardPage's PROJECT_CSS).
 *
 * SEPARATORS (menu.tmpl:42-47): a `<span class="seperator">` is emitted BEFORE
 * the `ahu_components` anchor and BEFORE the `ahu_accessories` anchor. The
 * second one is then hidden by `#menu-nav a[value=ahu_components] + .seperator
 * {display:none}` (project styles.css:7111-7113, added by 006906c44d), so only
 * one rule actually shows — dev's own belt-and-braces quirk, reproduced as-is
 * (markup + CSS both), which also means the DOM order span→anchor matters for
 * that adjacent-sibling selector to bite.
 *
 * [NEG] honored: no `partners` separator span, no `manufacturing` href branch
 * (no DB rows with those values exist).
 *
 * Renders an empty rail until tabs + project are loaded (MenuView waits on
 * get.permissions + create.model.project_main).
 */

/** menu.tmpl:42-47 — tab values that get a leading `.seperator` span. */
const SEPARATOR_BEFORE = new Set(['ahu_components', 'ahu_accessories']);
interface BoardMenuProps {
  tabs: IActualTab[] | undefined;
  project: IProjectMain | undefined;
  activeTab: string | null;
  onOpen: (value: string) => void;
}

const BoardMenu = ({ tabs, project, activeTab, onOpen }: BoardMenuProps) => (
  <div id="menu" className="col-xs-1">
    {tabs && project && (
      <nav id="menu-nav">
        {tabs.map((tab) => (
          <Fragment key={tab.value}>
            {SEPARATOR_BEFORE.has(tab.value) && <span className="seperator" />}
            <a
              className={`ka-has-tooltip ${
                tab.value === 'files' || tab.value === 'notes' ? 'bottom ' : ''
              }${activeTab === tab.value ? ' selected' : ''}`}
              tooltip={tab.name}
              value={tab.value}
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                onOpen(tab.value);
              }}
            >
              <i className={`icon-${tab.logo}`} />
              {tab.name}
            </a>
          </Fragment>
        ))}
        {!!project.is_can_set_project_logo && (
          <a
            className={`ka-has-tooltip bottom${
              activeTab === 'report_logo' ? ' selected' : ''
            }`}
            tooltip="Report Logo"
            value="report_logo"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              onOpen('report_logo');
            }}
          >
            <i className="material-symbols-outlined">settings_applications</i>
            Report Logo
          </a>
        )}
      </nav>
    )}
  </div>
);

export default BoardMenu;
